/**
 * 세계가 끝나면 — 남는 것과 사라지는 것 (UNI-Q130 · DESIGN 14.221).
 *
 * > 사용자 2026-09-14: *"이런 것들이 어딘가에 보기 편하게 남아있어야 하는데.."*
 *
 * 도움말 표가 이 한 표를 그린다. 옛 안내 넷(세계 종료 문구·도움말·브라우저 알림·푸시)이 규칙이 바뀐 뒤에도
 * 「창고는 남는다」를 가르치고 있었다 — 손으로 쓴 문장은 규칙이 바뀐 날 낡는다. 그래서 줄마다 **서버 코드의 실제 문장**
 * (`file` 의 `needle` 이 있어야 하고 `forbid` 가 없어야 한다)을 달고, `tools/check-world-reset.mjs` 가 그 문장이 살아 있는지 대조한다.
 * 규칙을 바꾸면 이 표와 reset.ts 가 함께 바뀌지 않는 한 게이트가 붉어진다.
 */
export const WORLD_END_KEPT = [
  { what: '캐릭터', detail: '이름·직업·슬롯은 그대로 남고 1레벨로 초기화된다. 새 세계에 들어가기 전 한 번 직업을 바꿀 수 있다.',
    file: 'src/world/reset.ts', needle: 'session = NULL, kills = 0, deaths = 0, power = 0', forbid: 'DELETE FROM characters' },
  // R122(2026-09-29) — 후원 보상은 후원자 칭호다. 자격은 원장에서 읽어 세계가 바뀌어도 그대로다(다시 주는 물건이 없다)
  { what: '후원자 칭호', detail: '후원한 계정은 새 세계에서도 도감 「후원자 칭호」를 쓸 수 있다. 전투력·장비·은화는 없다.',
    file: 'src/world/support-title.ts', needle: 'FROM support_titles WHERE account_id' },
  // H5(2026-10-04 · DESIGN 14.485) — 고른 칭호는 해금 기록(보스의 기억·후원 원장)이 남아 있으면 그대로 걸려 있다. 전에는 모든 계정에서 내려졌다
  { what: '고른 칭호', detail: '걸어 둔 칭호(보스의 기억·후원자)는 해금 기록이 남아 있으면 새 세계에서도 그대로 걸려 있다.',
    file: 'src/world/reset.ts', needle: "'bossTitle', ${titleKeptSql('accounts', src)}" },
  // H5 — 영혼석은 모든 계정에서 남는다. 전에는 그 세계에 들어온 계정만 옮겨 쉬는 계정이 잃었다
  { what: '영혼석', detail: '영혼석과 그동안 먹인 경험치가 계정에 그대로 남는다 — 그 세계에 들어오지 않은 계정도 같다.',
    file: 'src/world/reset.ts', needle: "'soul', json_extract(vault, '$.soul')" },
  { what: '도감', detail: '계정에 남는다.', file: 'src/world/reset.ts', forbid: 'account_discoveries' },
  { what: '계정 기록', detail: '건너온 세계 수·보너스 스탯 누계·연속 참여가 남는다.',
    file: 'src/world/reset.ts', needle: "'legacy', COALESCE(json_extract(vault, '$.legacy'), json_object())" },
  // R132(2026-09-29) — 정착금은 계정당 한 번. 세계가 끝나 1레벨이 된 몸은 봉인된다
  { what: '정착금 기록', detail: '정착금(Lv2~5)은 계정당 한 번이다 — 새 세계에서 다시 1레벨이 되어도, 캐릭터를 새로 만들어도 다시 나오지 않는다.',
    file: 'src/world/reset.ts', needle: '[SETTLE_SEALED_KEY]: true' },
  // R218(2026-09-30) — 배수·시점·누계를 문장에 적는다(전에는 「전투력에 따른 은화」뿐). 지금 이 세계의 값은 창고 창 맨 위·이 절 위 줄이 서버 carry 로 보인다
  { what: '새 세계 특전', detail: '마지막 날이 시작되는 순간의 계정 최고 전투력 × 배수(기본 10)만큼 은화를, 참여한 세계마다 보너스 스탯(기본 +30, 누계)을 새 창고에 넣는다. 지금 값은 창고 창 맨 위에 나온다.',
    file: 'src/world/reset.ts', needle: 'planNewWorldGrant' },
  { what: '연속 참여 은화', detail: '제1 세계부터 한 번도 빠지지 않고 참여한 계정은 끝난 세계 번호 × 100만 은화를 새 창고에 더 받는다.',
    file: 'src/world/reset.ts', needle: 'silver = worldN * 1_000_000;' },
  // F5(2026-10-06 · DESIGN 14.511) — 세계 랭커 특전. 전투력 배수 특전과 별개로 같은 새 창고에 들어가고, 휘장·동상은 다음 세계 한 판 동안만이다
  { what: '세계 랭커 특전', detail: '세계가 끝나는 순간 전투력 1~3위 계정(계정마다 가장 센 캐릭터 하나)은 상금 은화(기본 1위 5억·2위 3억·3위 2억)와 날개(기본 1위 불사조·2·3위 수호자)를 새 창고에 받는다. 다음 세계 내내 이름 옆에 「제N세계 1위」 휘장(금·은·동)이 걸리고 섬 마을 신전 앞에 그 모습 그대로 동상이 선다.',
    file: 'src/world/reset.ts', needle: 'applyRankerAward(vault, worldN, award, rankerCfg)' },
  // F5 ②(2026-10-06 · DESIGN 14.512) — 1위는 다음 세계 사냥터에 필드 보스로 남는다(그 세계 동안만 — 그다음 세계는 새 1위)
  { what: '세계 1위 필드 보스', detail: '세계가 끝나는 순간 전투력 1위 캐릭터가 다음 세계 내내 1위 레벨대 지상 사냥터에 그 모습 그대로 「제N세계 1위 ○○의 그림자」 필드 보스로 선다. 누구나(1위 본인도) 잡을 수 있고, 잡으면 누가 잡았는지 전 서버에 알려진다.',
    file: 'src/world/do.ts', needle: 'rankerBossSettings(worldN, this.rankerBossRankers!.list, cfg)' },
  { what: '집', detail: '구매한 집과 투자금(행운·회피·올스탯·무게)이 남는다. 다음 입장 때 같은 캐릭터가 다시 쓴다.',
    file: 'src/world/reset.ts', forbid: 'investment=0' },
  // F3(2026-10-06 · DESIGN 14.515) — 집이 여럿이면 어느 집이 현재 집인지도 건넌다. 전에는 번호순 첫 집이 붙어 큰 투자를 한 집의 효과가 빠졌다
  { what: '현재 집', detail: '집이 여럿이면 「현재 집으로」 고른 집(사서 현재 집이 된 집 포함)이 새 세계에서도 현재 집이고 그 집의 투자 효과가 붙는다. 고른 적이 없으면 투자 합이 가장 큰 집이다.',
    file: 'src/world/realty.ts', needle: '$.legacy.homePicks."' },
  { what: '길드', detail: '길드·회원·건물 레벨·금고·명예가 남는다.', file: 'src/world/reset.ts', forbid: 'guild_buildings' },
  { what: '명예 장비', detail: '장비는 사라지지만 산 값을 명예로 돌려받는다.', file: 'src/world/reset.ts', needle: 'planGuildHonorRefunds' },
  { what: '원주민회(NPC 길드)', detail: '건물 레벨·금고·회원이 남는다. NPC 회원은 같은 이름으로 다시 태어나 돌아온다.',
    file: 'src/world/native-organizations.ts', needle: 'function carryToWorld' },
  { what: 'NPC 왕도 별장', detail: '별장과 투자금이 남고, 같은 이름의 NPC가 값을 다시 내지 않고 되찾는다.',
    file: 'src/world/wander-housing.ts', needle: 'wanderer:n:' },
  { what: '세계 기록', detail: '비석(상위 세 명·처치·생존)과 세계 번호가 남는다.', file: 'src/world/reset.ts', needle: 'INSERT OR IGNORE INTO world_epitaphs' },
  { what: '명예의 전당', detail: '그 세계의 최고 레벨·최고 전투력·시련의 탑·부자·PK왕·보스 첫 처치가 영구히 새겨진다. 메뉴 › 세계 › 명예의 전당에서 본다.',
    file: 'src/world/reset.ts', needle: "leaders_json${hallStored ? ', hall_json' : ''}" },
];

export const WORLD_END_WIPED = [
  { what: '창고', detail: '은화·아이템·장비가 비워진다.',
    file: 'src/world/reset.ts', needle: "'junk', json_object(), 'consum', json_object(), 'equips', json_array(), 'silver', 0" },
  // R122(2026-09-29) — 옛 「후원 선물 복원」(세계마다 전설 +10 을 다시 넣기)은 폐지. 복원 원장을 다시 쓰면 이 줄이 붉어진다
  { what: '옛 후원 장비', detail: '예전에 후원으로 받은 전설 +10도 다른 장비처럼 사라지고 다시 들어오지 않는다(후원 보상은 이제 후원자 칭호다).',
    file: 'src/world/reset.ts', forbid: 'support_reward_restores' },
  { what: '캐릭터 성장', detail: '레벨·경험치·장비·가방·은화·스탯·스킬·서적·각인·무게 각인·펫이 초기화된다.',
    file: 'src/world/reset.ts', needle: 'silver = ?3, x = ?4, y = ?5, inv = ?6' },
  { what: '진행 중인 돈', detail: '미정산 판돈·미추첨 복권·미성사 현상금은 환불 없이 사라진다.', file: 'src/world/reset.ts', needle: 'BOUNTY_EXPIRE_SQL' },
  { what: '집 초대', detail: '초대한 거주자 자리가 풀린다.', file: 'src/world/reset.ts', needle: "DELETE FROM realty_residents" },
  { what: '보스·레어·경주 회차', detail: '보스 리스폰·드래곤 레어 정복·경주와 블랙잭 회차가 새로 시작한다.',
    file: 'src/world/do.ts', needle: 'RESPAWN_KEY, BOSS_CHALLENGE_KEY, RACE_NO_KEY' },
  // DESIGN 14.606(2026-10-21 사용자 결정) — 왕도도 세계마다 새로: 지난 세계의 매입 물건·은화가 원주민 배분으로 새 세계에 넘어오지 않는다
  { what: '왕도 금고·매입 재고', detail: '왕도 금고의 은화와 원주민 장터가 사들인 재고(왕의 배분 대기)가 비워진다. 세금·지급·매각·기부 기록은 남는다.',
    file: 'src/world/reset.ts', needle: "out.push(db.prepare('DELETE FROM native_market_stock'))" },
  { what: 'NPC의 몸', detail: '1레벨부터 다시 자란다. 원주민회 회원·별장 주인의 이름이 먼저 돌아온다.',
    file: 'src/world/do.ts', needle: 'prepareReturningResidents' },
];

/** 도움말 표 — 두 열(남는 것 / 사라지는 것). 한 줄 = 무엇 + 설명. */
export function worldEndTable(esc, carry = null) {
  const rows = (list) => list.map((r) => `<li><b>${esc(r.what)}</b> — ${esc(r.detail)}</li>`).join('');
  const wipeVault = typeof carry?.wipeVault === 'boolean' ? carry.wipeVault : null;
  // 이번 회차 특전의 실제 0/액수는 바로 위 서버 carry 안내가 보여 준다.
  const kept = WORLD_END_KEPT.filter((r) => (wipeVault === true || r.what !== '명예 장비') && r.what !== '새 세계 특전');
  const wiped = WORLD_END_WIPED.filter((r) => wipeVault === true || !['창고', '옛 후원 장비'].includes(r.what));
  if (wipeVault === false) {
    kept.push({ what: '창고', detail: '은화·아이템·장비가 그대로 남는다. 지급 대상 보상이 있으면 여기에 더해진다.' });
    wiped.push({ what: '몸의 명예 장비', detail: '가방의 장비와 함께 초기화된다. 명예 환급은 다음 창고 비우는 세계 전환까지 미뤄진다.' });
  }
  const pending = wipeVault === null ? '<p>이번 세계의 창고 보존 설정은 아직 확인되지 않았다. 다시 입장해 창고를 확인해 달라.</p>' : '';
  return `${pending}<div class="world-end-rules"><div><b>남는 것</b><ul>${rows(kept)}</ul></div>`
    + `<div><b>사라지는 것</b><ul>${rows(wiped)}</ul></div></div>`;
}
